import { currentCoachUpcomingTopicsQuery } from "@/Components/ClientCoachInfo/UpcomingTopics/upcomingTopicsData";
import { BoardAccessContext } from "@/Contexts/BoardAccessContext";
import { ClientExercisesContext } from "@/Contexts/ClientExercisesContext";
import { GiftCoachingContext } from "@/Contexts/GiftCoachingContext";
import { useClient } from "@/utils/identity";
import { navigateTo } from "@/utils/navigation";
import { useQuery } from "@tanstack/react-query";
import { useContext } from "react";
import { AiOutlineQuestionCircle } from "react-icons/ai";
import { BiCalendar, BiMessage } from "react-icons/bi";
import { FiGift } from "react-icons/fi";
import { HiOutlineSwitchHorizontal } from "react-icons/hi";
import { HiOutlineRocketLaunch } from "react-icons/hi2";
import { LuHistory, LuMessageSquareMore, LuSettings } from "react-icons/lu";
import { PiClipboardTextBold } from "react-icons/pi";
import { RiPushpinLine } from "react-icons/ri";
import { TbTargetArrow } from "react-icons/tb";
import {
    logOutNavItem,
    mobileAppNavItem,
    videoTestNavItem,
} from "./sharedNavItems";
import { NavItem } from "./types";

/*
 * Bar-eligible items are listed in priority order: the first visible ones win
 * the available bar slots and the rest overflow into the More menu, after any
 * "beforeOverflow" menu items.
 */
export default function useClientNavItems(): NavItem[] {
    const client = useClient();

    const canLoadUpcomingTopics = !!(
        client.email_verified_at || client.phone_verified_at
    );
    /*
     * A failed load only hides the Upcoming tab instead of putting every
     * client page on the error screen.
     */
    const { data: currentCoachUpcomingTopics = [] } = useQuery({
        ...currentCoachUpcomingTopicsQuery,
        enabled: canLoadUpcomingTopics,
        throwOnError: false,
    });

    const { hasExercises, incompleteExercisesCount } = useContext(
        ClientExercisesContext,
    );
    const { goalsBoard } = useContext(BoardAccessContext);
    const { showGiftCoachingModal, canDoReferrals } =
        useContext(GiftCoachingContext);

    const isPaymentSetupIncomplete = client.has_payment_setup === false;
    const hasAnyUpcomingTopics = currentCoachUpcomingTopics.length > 0;

    return [
        {
            placement: "bar",
            icon: BiMessage,
            name: "Messages",
            onClick: () => navigateTo(route("chat")),
            routeName: "chat",
            selected: route().current() === "chat",
        },
        {
            placement: "bar",
            icon: RiPushpinLine,
            name: "Upcoming",
            visible: hasAnyUpcomingTopics,
            onClick: () => navigateTo(route("client.upcoming-topics")),
            routeName: "client.upcoming-topics",
            selected: route().current() === "client.upcoming-topics",
        },
        {
            placement: "bar",
            icon: BiCalendar,
            name: "Schedule",
            onClick: () => navigateTo(route("client.my-sessions")),
            routeName: "client.my-sessions",
            selected: ["coach.scheduling", "client.my-sessions"].includes(
                route().current() ?? "",
            ),
        },
        mobileAppNavItem(),
        {
            placement: "bar",
            icon: TbTargetArrow,
            name: "Goals Board",
            visible: !!goalsBoard?.is_unlocked,
            onClick: () => {
                if (!goalsBoard) {
                    return;
                }
                navigateTo(
                    route("board.get", {
                        otherUserId: goalsBoard.coach.id,
                    }),
                );
            },
            routeName: "board.get",
            selected: route().current() === "board.get",
        },
        {
            placement: "bar",
            icon: PiClipboardTextBold,
            name: "Exercises",
            visible: hasExercises,
            onClick: () => navigateTo(route("client.exercises.index")),
            routeName: "client.exercises.index",
            selected: route().current() === "client.exercises.index",
            badgeNumbers: [incompleteExercisesCount],
        },
        {
            placement: "menu",
            menuSlot: "beforeOverflow",
            icon: HiOutlineRocketLaunch,
            name: "Start Coaching",
            visible: isPaymentSetupIncomplete,
            onClick: () => navigateTo(route("second-bite.payment")),
            routeName: "second-bite.payment",
            selected: route().current() === "second-bite.payment",
        },
        {
            placement: "menu",
            menuSlot: "beforeOverflow",
            icon: LuSettings,
            name: "Account",
            onClick: () => navigateTo(route("client.profile.edit")),
            routeName: "client.profile.edit",
            selected: route().current() === "client.profile.edit",
        },
        {
            placement: "menu",
            icon: LuHistory,
            name: "Past Sessions",
            onClick: () => navigateTo(route("client.past-sessions")),
            routeName: "client.past-sessions",
            selected: route().current() === "client.past-sessions",
        },
        {
            placement: "menu",
            icon: FiGift,
            name: "Gift a free trial",
            visible: canDoReferrals ?? false,
            onClick: () => showGiftCoachingModal(),
            selected: route().current() === "gift-free-trial",
        },
        {
            placement: "menu",
            icon: HiOutlineSwitchHorizontal,
            name: "Change Coach",
            visible: isPaymentSetupIncomplete,
            onClick: () => navigateTo(route("switch-page.index")),
            routeName: "switch-page.index",
            selected: route().current() === "switch-page.index",
        },
        {
            placement: "menu",
            icon: AiOutlineQuestionCircle,
            name: "FAQ",
            onClick: () => navigateTo(route("faq")),
            routeName: "faq",
            selected: route().current() === "faq",
        },
        {
            placement: "menu",
            icon: LuMessageSquareMore,
            name: "Contact Us",
            onClick: () => navigateTo(route("client.contact-us.show")),
            routeName: "client.contact-us.show",
            selected: route().current() === "client.contact-us.show",
        },
        videoTestNavItem(),
        logOutNavItem(),
    ];
}
